import React, { useEffect, useState, useCallback } from "react";
import { server } from "../../server";
import { toast } from "react-toastify";
import axios from "axios";
import { Pagination } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  FiFileText, 
  FiSearch, 
  FiPlus, 
  FiEye, 
  FiEdit2, 
  FiLayers, 
  FiClock, 
  FiBookOpen,
  FiFilter
} from "react-icons/fi";
import useAuth from "../../useAuth";

export default function DocsList() {
  const navigate = useNavigate();
  const { token } = useAuth();
  
  const [pageNo, setPageNo] = useState(1);
  const [pageSize] = useState(10);
  const [docsCount, setDocsCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useState("");
  const [docsList, setDocsList] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("All");

  const handleGetDocs = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${server}/docs/all`, {
        headers: {
          Authorization: `Bearer ${token || localStorage.getItem("token")}`,
        },
        params: {
          pageNo: pageNo,
          pageSize: pageSize,
          deleted: false,
          searchParams: searchParams,
        },
      });
      setDocsList(data?.data || []);
      setDocsCount(data?.count || 0);
      setLoading(false);
    } catch (e) {
      setLoading(false);
      toast.error(e?.response?.data || e?.message);
    }
  }, [pageNo, pageSize, searchParams, token]);

  useEffect(() => {
    handleGetDocs();
  }, [handleGetDocs]);

  // Extract unique categories from current lists for filtering
  const categoriesList = ["All", ...new Set(docsList.map(doc => doc.category || "Documentation").filter(Boolean))];

  // Filter docs locally based on selected category if category isn't "All"
  const filteredDocs = selectedCategory === "All" 
    ? docsList 
    : docsList.filter(doc => (doc.category || "Documentation") === selectedCategory);

  // Dynamic Badge Color mapping based on string value
  const getCategoryBadgeStyle = (category) => {
    const text = (category || "Documentation").toLowerCase();
    if (text.includes("guide") || text.includes("how")) {
      return "bg-teal-50 text-teal-700 border-teal-200";
    }
    if (text.includes("api") || text.includes("tech")) {
      return "bg-indigo-50 text-indigo-700 border-indigo-200";
    }
    if (text.includes("design") || text.includes("ui")) {
      return "bg-pink-50 text-pink-700 border-pink-200";
    }
    if (text.includes("setup") || text.includes("install")) {
      return "bg-amber-50 text-amber-700 border-amber-200";
    }
    return "bg-slate-50 text-slate-700 border-slate-200";
  };

  return (
    <div className="min-h-screen bg-slate-50/50 p-6 font-sans">
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.4 }}
        className="max-w-6xl mx-auto"
      >
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-blue-50 text-blue-600 rounded-xl">
                <FiBookOpen className="text-2xl" />
              </div>
              <div>
                <h1 className="text-3xl font-extrabold text-slate-800 tracking-tight">
                  Knowledge Base
                </h1>
                <p className="text-sm text-slate-500 mt-1">
                  Manage and view project documentations, specifications, and revision history.
                </p>
              </div>
            </div>
          </div>

          <button
            onClick={() => navigate("../addNewDoc")}
            className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white px-5 py-2.5 rounded-xl font-semibold shadow-sm hover:shadow-md transition duration-200 text-sm whitespace-nowrap self-start md:self-auto"
          >
            <FiPlus className="text-lg" />
            Add Document
          </button>
        </div>

        {/* Stats Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* Card 1: Total Documents */}
          <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm flex items-center gap-5">
            <div className="p-4 bg-blue-50 text-blue-600 rounded-2xl">
              <FiFileText className="text-2xl" />
            </div>
            <div>
              <span className="block text-xs font-semibold uppercase tracking-wider text-slate-400">
                Total Documents
              </span>
              <span className="text-2xl font-bold text-slate-800">
                {docsCount}
              </span>
            </div>
          </div>

          {/* Card 2: Unique Categories */}
          <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm flex items-center gap-5">
            <div className="p-4 bg-teal-50 text-teal-600 rounded-2xl">
              <FiLayers className="text-2xl" />
            </div>
            <div>
              <span className="block text-xs font-semibold uppercase tracking-wider text-slate-400">
                Active Categories
              </span>
              <span className="text-2xl font-bold text-slate-800">
                {categoriesList.length - 1 > 0 ? categoriesList.length - 1 : 1}
              </span>
            </div>
          </div>

          {/* Card 3: Recent Activity */}
          <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm flex items-center gap-5">
            <div className="p-4 bg-amber-50 text-amber-600 rounded-2xl">
              <FiClock className="text-2xl" />
            </div>
            <div className="min-w-0 flex-1">
              <span className="block text-xs font-semibold uppercase tracking-wider text-slate-400">
                Latest Document
              </span>
              <span className="text-sm font-semibold text-slate-700 truncate block mt-0.5">
                {docsList.length > 0 ? docsList[0].title : "No documents yet"}
              </span>
            </div>
          </div>
        </div>

        {/* Filters Panel */}
        <div className="bg-white p-4 rounded-2xl border border-slate-100 shadow-sm mb-6 flex flex-col md:flex-row gap-4 items-center justify-between">
          <div className="relative w-full md:w-96">
            <FiSearch className="absolute left-3.5 top-3.5 text-slate-400 pointer-events-none" />
            <input
              type="text"
              placeholder="Search documents by title or category..."
              value={searchParams}
              onChange={(e) => {
                setSearchParams(e.target.value);
                setPageNo(1);
              }}
              className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm transition"
            />
          </div>

          <div className="flex items-center gap-2 w-full md:w-auto justify-end">
            <FiFilter className="text-slate-400" />
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-3 py-2 border border-slate-200 rounded-xl text-sm text-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition cursor-pointer"
            >
              <option value="All">All Categories</option>
              {categoriesList.filter(c => c !== "All").map((cat, i) => (
                <option key={i} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Documentations Table Card */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/70 border-b border-slate-100 text-slate-500 text-xs font-bold uppercase tracking-wider">
                  <th className="px-6 py-4">Doc Title</th>
                  <th className="px-6 py-4">Category</th>
                  <th className="px-6 py-4 text-center">Version</th>
                  <th className="px-6 py-4 text-center">Revisions</th>
                  <th className="px-6 py-4">Created By</th>
                  <th className="px-6 py-4">Created At</th>
                  <th className="px-6 py-4 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-sm text-slate-600">
                {loading ? (
                  // Skeleton Loading State
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i} className="animate-pulse">
                      <td className="px-6 py-4">
                        <div className="h-4 bg-slate-100 rounded w-48 mb-2" />
                        <div className="h-3 bg-slate-100 rounded w-24" />
                      </td>
                      <td className="px-6 py-4">
                        <div className="h-5 bg-slate-100 rounded-full w-20" />
                      </td>
                      <td className="px-6 py-4">
                        <div className="h-5 bg-slate-100 rounded w-8 mx-auto" />
                      </td>
                      <td className="px-6 py-4">
                        <div className="h-5 bg-slate-100 rounded-full w-8 mx-auto" />
                      </td>
                      <td className="px-6 py-4">
                        <div className="h-4 bg-slate-100 rounded w-24" />
                      </td>
                      <td className="px-6 py-4">
                        <div className="h-4 bg-slate-100 rounded w-20" />
                      </td>
                      <td className="px-6 py-4">
                        <div className="h-8 bg-slate-100 rounded w-16 mx-auto" />
                      </td>
                    </tr>
                  ))
                ) : filteredDocs.length === 0 ? (
                  // Empty State
                  <tr>
                    <td colSpan={7} className="px-6 py-16 text-center">
                      <div className="flex flex-col items-center justify-center max-w-sm mx-auto">
                        <div className="p-4 bg-slate-50 text-slate-400 rounded-full mb-4">
                          <FiFileText size={32} />
                        </div>
                        <h3 className="text-base font-bold text-slate-800">
                          No documents found
                        </h3>
                        <p className="text-xs text-slate-500 mt-1 mb-6">
                          There are no documents matching your search or category filter. Try clearing filters or create a new one.
                        </p>
                        <button
                          onClick={() => {
                            setSearchParams("");
                            setSelectedCategory("All");
                          }}
                          className="px-4 py-2 border border-slate-200 rounded-xl text-xs font-semibold text-slate-600 hover:bg-slate-50 active:bg-slate-100 transition"
                        >
                          Clear Filters
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  // Data List
                  filteredDocs.map((item) => (
                    <tr
                      key={item.id}
                      onClick={() => navigate("../doc", { state: { docId: item?.id } })}
                      className="hover:bg-slate-50/80 transition cursor-pointer group"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-slate-50 text-slate-400 group-hover:bg-blue-50 group-hover:text-blue-500 rounded-lg transition duration-200">
                            <FiFileText className="text-lg" />
                          </div>
                          <span className="font-semibold text-slate-800 group-hover:text-blue-600 transition">
                            {item.title || "Untitled Document"}
                          </span>
                        </div>
                      </td>

                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getCategoryBadgeStyle(item.category)}`}>
                          {item.category || "Documentation"}
                        </span>
                      </td>

                      <td className="px-6 py-4 text-center">
                        <span className="inline-flex items-center px-2 py-0.5 rounded bg-blue-50 text-blue-700 text-xs font-bold font-mono">
                          v{item.version || "1.0"}
                        </span>
                      </td>

                      <td className="px-6 py-4 text-center">
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-slate-100 text-slate-600 text-xs font-semibold">
                          {item.revisions?.length || 1}
                        </span>
                      </td>

                      <td className="px-6 py-4 font-medium text-slate-700">
                        {item.createdByName || `User #${item.createdBy}`}
                      </td>

                      <td className="px-6 py-4 text-slate-500 text-xs font-mono">
                        {item.createdAt
                          ? new Date(item.createdAt).toLocaleDateString("en-IN", {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })
                          : "---"}
                      </td>

                      <td
                        className="px-6 py-4 text-center"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => navigate("../doc", { state: { docId: item?.id } })}
                            title="View Document"
                            className="p-1.5 hover:bg-slate-100 text-slate-500 hover:text-slate-700 rounded-lg transition"
                          >
                            <FiEye className="text-base" />
                          </button>
                          <button
                            onClick={() => navigate("../editDoc", { state: { docId: item?.id } })}
                            title="Edit Document"
                            className="p-1.5 hover:bg-slate-100 text-slate-500 hover:text-blue-600 rounded-lg transition"
                          >
                            <FiEdit2 className="text-base" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {docsCount > pageSize && (
            <div className="flex justify-between items-center px-6 py-4 border-t border-slate-100">
              <span className="text-xs text-slate-500">
                Showing {Math.min((pageNo - 1) * pageSize + 1, docsCount)} to {Math.min(pageNo * pageSize, docsCount)} of {docsCount} documents
              </span>
              <Pagination
                count={Math.ceil(docsCount / pageSize)}
                page={pageNo}
                onChange={(event, value) => setPageNo(value)}
                color="primary"
                shape="rounded"
                size="small"
              />
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
